import { useCurrentUser } from '../../../features/auth/api';
import {
  useGetOrganization,
  useCreateOrganization,
  useUpdateOrganization,
  useArchiveOrganization,
  useUnarchiveOrganization,
  useUploadOrgAgreement,
  useDeleteOrgAgreement,
  useDownloadOrgAgreement,
} from '../../../features/organization/api';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from '../../../components/AddressAutocomplete/AddressAutocomplete';
import WeeklyOrderReminderCard from './WeeklyOrderReminderCard';
import WeeklyReminderRecipients from './WeeklyReminderRecipients';

export default function OrganizationTab() {
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const hasOrg = !!user?.organization && Object.keys(user.organization || {}).length > 0;
  const ownsOrg = ['ORG_OWNER'].includes(user?.type);

  const { data: org, isLoading: orgLoading } = useGetOrganization(hasOrg);
  const createOrg = useCreateOrganization();
  const updateOrg = useUpdateOrganization();
  const archiveOrg = useArchiveOrganization();
  const unarchiveOrg = useUnarchiveOrganization();
  const nav = useNavigate();

  const [form, setForm] = useState({
    name: '',
    legalName: '',
    phone: '',
    email: '',
    website: '',
    hidePrice: false,
    perItemPricing: false,
    pricingEnabled: false,
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name ?? '',
        legalName: org.legalName ?? '',
        phone: org.phone ?? '',
        email: org.email ?? '',
        website: org.website ?? '',
        hidePrice: !!org.hidePrice,
        perItemPricing: !!org.perItemPricing,
        pricingEnabled: !!org.pricingEnabled,
        addressLine1: org.addressLine1 ?? '',
        addressLine2: org.addressLine2 ?? '',
        city: org.city ?? '',
        state: org.state ?? '',
        postalCode: org.postalCode ?? '',
        country: org.country ?? '',
      });
    } else if (!hasOrg) {
      setForm({
        name: '',
        legalName: '',
        phone: '',
        email: '',
        website: '',
        hidePrice: false,
        perItemPricing: false,
        pricingEnabled: false,
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      });
    }
  }, [org, hasOrg]);

  const isArchived = useMemo(() => !!org?.deletedAt, [org]);
  const loading = userLoading || (hasOrg && orgLoading);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = () => {
    if (!form.name.trim()) return toast.warn('Organization name is required.');
    const payload = {
      name: form.name.trim(),
      legalName: form.legalName?.trim() || null,
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      website: form.website?.trim() || null,
      hidePrice: !!form.hidePrice,
      perItemPricing: !!form.perItemPricing,
      pricingEnabled: !!form.pricingEnabled,
      addressLine1: form.addressLine1?.trim() || null,
      addressLine2: form.addressLine2?.trim() || null,
      city: form.city?.trim() || null,
      state: form.state?.trim() || null,
      postalCode: form.postalCode?.trim() || null,
      country: form.country?.trim() || null,
    };

    if (!hasOrg || !ownsOrg) {
      createOrg.mutate(payload, {
        onSuccess: () => {
          toast.success('Organization created.');
          nav('/organization/warehouses');
        },
        onError: () => {
          toast.error('Failed to create organization.');
        },
      });
    } else {
      updateOrg.mutate(payload, {
        onSuccess: () => {
          toast.success('Organization updated.');
        },
        onError: () => {
          toast.error('Failed to update organization.');
        },
      });
    }
  };

  const onToggleArchive = () => {
    if (!hasOrg) return;
    if (!ownsOrg) return toast.warn('Only the org owner can archive/unarchive.');
    if (isArchived) {
      unarchiveOrg.mutate(undefined, {
        onSuccess: () => toast.success('Organization unarchived.'),
        onError: () => toast.error('Failed to unarchive organization.'),
      });
    } else {
      archiveOrg.mutate(undefined, {
        onSuccess: () => toast.success('Organization archived.'),
        onError: () => toast.error('Failed to archive organization.'),
      });
    }
  };

  // const onTogglehidePrice = (e) => {
  //   const checked = e.target.checked;
  //   setForm((f) => ({ ...f, hidePrice: checked }));
  // };

  const onTogglehideCsp = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setForm((f) => ({ ...f, perItemPricing: checked }));
  };

  const onTogglePricingEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setForm((f) => ({ ...f, pricingEnabled: checked }));
  };

  const uploadAgreement = useUploadOrgAgreement();
  const deleteAgreement = useDeleteOrgAgreement();
  const downloadAgreement = useDownloadOrgAgreement();
  const [agreeKey, setAgreeKey] = useState(0);

  const hasTemplate = !!org?.agreementTemplateUrl;

  const onPickAgreement = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/pdf$/i.test(file.name) || !/pdf/i.test(file.type))
      return toast.error('Please select a PDF.');
    try {
      await uploadAgreement.mutateAsync(file);
      toast.success('Agreement template uploaded.');
      setAgreeKey((k) => k + 1);
    } catch {
      toast.error('Upload failed.');
    }
  };

  const onRemoveAgreement = async () => {
    if (!window.confirm('Remove the current agreement template?')) return;
    try {
      await deleteAgreement.mutateAsync();
      toast.success('Agreement template removed.');
    } catch {
      toast.error('Failed to remove template.');
    }
  };

  const onDownloadAgreement = async () => {
    try {
      const blob = await downloadAgreement.mutateAsync();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Agreement_Template.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed.');
    }
  };

  if (loading) {
    return (
      <div className="card">
        <h2 className="card__title">Organization</h2>
        <p className="card__subtitle">Company-level settings</p>
        <div className="skeleton" style={{ height: 140, width: '100%', marginTop: 8 }} />
      </div>
    );
  }

  const inputsDisabled = isArchived || !ownsOrg;

  return (
    <div className="card">
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}
      >
        <div>
          <h2 className="card__title">Organization</h2>
          <p className="card__subtitle">
            {hasOrg
              ? ownsOrg
                ? 'You own this organization.'
                : 'You are a member; saving will create your own organization.'
              : 'No organization yet. Create one below.'}
          </p>
        </div>

        {hasOrg && (
          <span
            className="badge"
            style={{
              padding: '6px 10px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 12,
              color: isArchived ? '#991b1b' : '#065f46',
              background: isArchived ? '#fee2e2' : '#dcfce7',
              border: `1px solid ${isArchived ? '#fecaca' : '#bbf7d0'}`,
            }}
          >
            {isArchived ? 'Archived' : 'Active'}
          </span>
        )}
      </div>

      <div className="grid grid--2" style={{ marginTop: 12 }}>
        <div className="form-control">
          <label htmlFor="orgName">Organization Name *</label>
          <input
            id="orgName"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Bulk Material PRO"
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="legalName">Legal Name</label>
          <input
            id="legalName"
            name="legalName"
            value={form.legalName}
            onChange={onChange}
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder="+1 (555) 123-4567"
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="website"
            value={form.website}
            onChange={onChange}
            placeholder="https://example.com"
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="addressLine1">Address Line 1</label>
          <AddressAutocomplete
            value={form.addressLine1}
            onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
            onPlaceSelected={(fields) =>
              setForm((f) => ({
                ...f,
                addressLine1: fields.line1,
                city: fields.city,
                state: fields.state,
                postalCode: fields.postalCode,
                country: fields.country,
              }))
            }
            disabled={inputsDisabled}
            placeholder="123 Main St"
          />
        </div>

        <div className="form-control" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="addressLine2">Address Line 2</label>
          <input
            id="addressLine2"
            name="addressLine2"
            value={form.addressLine2}
            onChange={onChange}
            placeholder="Suite 100"
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="city">City</label>
          <input
            id="city"
            name="city"
            value={form.city}
            onChange={onChange}
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="state">State</label>
          <input
            id="state"
            name="state"
            value={form.state}
            onChange={onChange}
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="postalCode">Postal Code</label>
          <input
            id="postalCode"
            name="postalCode"
            value={form.postalCode}
            onChange={onChange}
            disabled={inputsDisabled}
          />
        </div>

        <div className="form-control">
          <label htmlFor="country">Country</label>
          <input
            id="country"
            name="country"
            value={form.country}
            onChange={onChange}
            disabled={inputsDisabled}
          />
        </div>
        {/* <div className="form-control" style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!form.hidePrice}
              onChange={onTogglehidePrice}
              disabled={inputsDisabled}
            />
            Disable Price (hide/disable pricing throughout the app)
          </label>
        </div> */}
        <div className="form-control" style={{ gridColumn: '1 / -1' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 8,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!form.perItemPricing}
              onChange={onTogglehideCsp}
              disabled={inputsDisabled}
            />
            Customer-Specific Catalog & Pricing
          </label>
        </div>
        <div className="form-control" style={{ gridColumn: '1 / -1' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 8,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!form.pricingEnabled}
              onChange={onTogglePricingEnabled}
              disabled={inputsDisabled}
            />
            Show pricing (regional pricing feature)
          </label>
          <div className="muted tiny" style={{ marginLeft: 28 }}>
            Master switch — when on, order estimates appear for org admins. Customers still only see
            pricing if it’s enabled per-customer.
          </div>
        </div>
      </div>

      <div className="card card--bleed-top" style={{ marginTop: 16 }}>
        <h3>Documents</h3>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-control">
            <label>Agreement template (PDF)</label>

            {!hasTemplate ? (
              <>
                <input
                  key={agreeKey}
                  type="file"
                  accept="application/pdf"
                  onChange={onPickAgreement}
                  disabled={uploadAgreement.isPending || isArchived}
                />
                <div className="muted" style={{ marginTop: 4 }}>
                  No template uploaded.
                </div>
              </>
            ) : (
              <>
                <div className="muted" style={{ marginBottom: 8 }}>
                  Template on file.
                </div>
                <div className="actions" style={{ gap: 8 }}>
                  <button
                    className="btn btn--ghost"
                    onClick={onDownloadAgreement}
                    disabled={downloadAgreement.isPending}
                  >
                    {downloadAgreement.isPending ? 'Downloading…' : 'Download'}
                  </button>
                  <button
                    className="btn btn--danger"
                    onClick={onRemoveAgreement}
                    disabled={deleteAgreement.isPending || isArchived}
                  >
                    {deleteAgreement.isPending ? 'Removing…' : 'Remove'}
                  </button>
                </div>
                <div className="muted" style={{ marginTop: 8 }}>
                  Upload a new file to replace the existing template:
                </div>
                <input
                  key={agreeKey}
                  type="file"
                  accept="application/pdf"
                  onChange={onPickAgreement}
                  disabled={uploadAgreement.isPending || isArchived}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="actions">
        <button
          className="btn btn--primary"
          onClick={onSave}
          disabled={inputsDisabled || createOrg.isPending || updateOrg.isPending}
        >
          {createOrg.isPending || updateOrg.isPending
            ? hasOrg && ownsOrg
              ? 'Updating...'
              : 'Creating...'
            : hasOrg && ownsOrg
              ? 'Save Changes'
              : 'Create Organization'}
        </button>

        <button
          className="btn btn--ghost"
          onClick={onToggleArchive}
          disabled={!hasOrg || !ownsOrg || archiveOrg.isPending || unarchiveOrg.isPending}
        >
          {archiveOrg.isPending || unarchiveOrg.isPending
            ? isArchived
              ? 'Unarchiving...'
              : 'Archiving...'
            : isArchived
              ? 'Unarchive'
              : 'Archive'}
        </button>
      </div>

      {hasOrg && (
        <div style={{ marginTop: 16 }}>
          <WeeklyOrderReminderCard />
        </div>
      )}

      {hasOrg && (
        <div style={{ marginTop: 16 }}>
          <WeeklyReminderRecipients />
        </div>
      )}
    </div>
  );
}
